import { useContext, useRef } from "react";
import { useAsyncSupabaseRequest } from "~/hooks/useAsyncRequest";
import { useDebounce } from "~/hooks/useDebounce";
import type { Tag } from "~/types/tags.types";
import Error from "../form/Error";
import InputLabel from "./InputLabel";
import Typeahead from "./Typeahead";
import IconInputArrowDown from "~/assets/icon-input-arrow-down.svg";

type FormProperties = {
  context: React.Context<any>;
  property: string;
  options: { name: string; value: string }[];
  hasDefaultValue?: boolean;
  label?: string;
  required?: boolean;
  assetsData?: string;
  index?: number;
  requests?: ((data: string) => any)[];
  shortRequests?: ((data: string) => any)[];
  sortResults?: Function;
  transformationCallback?: Function;
  uniqueIdentifier?: boolean;
  uniqueError?: string;
  displayUniqueError?: boolean;
  removeShadow?: boolean;
  className?: string;
  withBorder?: boolean;
  textDirection?: string;
  manulAdd?: boolean;
  ["data-cy"]?: string;
  containerClassName?: string;
};

const FormTypeaheadInput = ({
  context,
  property,
  options,
  hasDefaultValue,
  label,
  required,
  assetsData,
  index,
  requests,
  shortRequests,
  sortResults,
  transformationCallback,
  uniqueIdentifier,
  uniqueError,
  displayUniqueError,
  removeShadow,
  className,
  withBorder,
  textDirection,
  manulAdd = false,
  containerClassName,
  ...props
}: FormProperties) => {
  const { state, dispatch, fiat, errors, setErrors } = useContext(context);
  const { isLoading, setIsLoading, fetchData } = useAsyncSupabaseRequest();
  const { set, clear } = useDebounce();
  const lastAsyncFetchFilter = useRef<string>();

  const disabledArray = state?.disabled;

  const isDisabled = disabledArray?.find(
    (keyword: string) => keyword === property
  );

  let value = "";
  if (assetsData && index !== undefined) {
    const cellValue = state?.[assetsData]?.[index]?.[property];
    value = uniqueIdentifier
      ? cellValue
      : cellValue?.name || cellValue?.value || "";
  } else {
    const cellValue = state?.[property];
    value = uniqueIdentifier
      ? cellValue
      : cellValue?.name || cellValue?.value || "";
  }

  const asyncFetch = async (filter: string, pickedOption: Tag) => {
    if (
      pickedOption?.value === filter ||
      (pickedOption?.name === filter && pickedOption?.value)
    )
      return;
    if (!requests || !transformationCallback || filter === "") {
      clear();
      return;
    }
    let data = "";
    lastAsyncFetchFilter.current = filter;
    if (filter?.length < 4 && shortRequests) {
      data = await fetchData(filter, shortRequests);
    } else {
      data = await fetchData(filter, requests);
    }
    if (!data) {
      dispatch({
        type: "options",
        payload: index !== undefined ? { index, data: [] } : [],
      });
      return;
    }

    const transformedData = transformationCallback
      ? transformationCallback(data, fiat)
      : data;

    const sortedData = sortResults
      ? sortResults(filter, transformedData)
      : transformedData;

    dispatch({
      type: "options",
      payload: index !== undefined ? { index, data: sortedData } : sortedData,
    });
  };

  const errorChanges = (property?: string) => {
    if (!property) return;

    if (errors[property]) {
      const newErrors = { ...errors };
      delete newErrors[property];
      setErrors(newErrors);
    }
  };
  return (
    <div>
      {label && (
        <InputLabel label={label} uneditable={isDisabled} required={required} />
      )}
      <div
        className={`relative flex items-center justify-around rounded-md bg-surface px-3 py-2 ${
          withBorder && !isDisabled ? "border-[1px] border-primary" : ""
        } ${!removeShadow && "shadow-md"} ${containerClassName || ""}`}
      >
        <Typeahead
          position="static"
          className={className}
          options={options}
          filter={value || ""}
          callback={(data) => {
            errorChanges(property);
            errorChanges(uniqueError);
            const pickedOption = options.find(
              (option) => option.name === data
            ) || { name: data, value: "" };

            if (requests) {
              // TODO: improve trigger
              setIsLoading(undefined);
              set(() => asyncFetch(data, pickedOption), 1000);
            }

            dispatch({
              type: property,
              payload:
                index !== undefined
                  ? { index, data: pickedOption }
                  : pickedOption,
            });
          }}
          hasDefaultValue={hasDefaultValue}
          isLoading={isLoading}
          requests={requests?.length}
          disabled={!!isDisabled}
          textDirection={textDirection}
          manulAdd={manulAdd}
          data-cy={props["data-cy"]}
        />
        <img src={IconInputArrowDown} alt="arrow down" />
      </div>
      {errors?.[(displayUniqueError && uniqueError) || property] && (
        <Error
          error={errors?.[(displayUniqueError && uniqueError) || property]}
        />
      )}
    </div>
  );
};

export default FormTypeaheadInput;
